"use client";

import { useTranslations } from "next-intl";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { tenderCounters } from "./heroTimeline";

export const TENDER_STAGES = ["announced", "bids", "evaluation", "winner"] as const;

const SAMPLE_TENDER = {
  company: "Grand-Mart MMC",
  location: "Bakı",
  deadline: "24.01.24-dək",
  title: "Market şəbəkəsi üçün vitrin soyuducuların təchizatı",
};

const BID_STAGE = 1;
const WINNER_STAGE = 3;

interface TenderStageCardProps {
  stage: number;
  progress: number;
  showChips: boolean;
  motion: boolean;
  onSelectStage: (stage: number) => void;
  className?: string;
}

export default function TenderStageCard({
  stage,
  progress,
  showChips,
  motion,
  onSelectStage,
  className,
}: TenderStageCardProps) {
  const t = useTranslations("home.card");
  const counters = tenderCounters(stage, progress);
  const overall = (stage + progress) / TENDER_STAGES.length;

  return (
    <article
      data-phase={motion ? "play" : "idle"}
      className={cn(
        "hero-slide relative w-full max-w-[540px] flex-1 rounded-[24px] bg-brand-navy p-6 text-brand-cream shadow-[14px_14px_0_rgba(15,27,45,0.14)] sm:p-8",
        className,
      )}
      aria-label={SAMPLE_TENDER.title}
    >
      {showChips && stage === BID_STAGE && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-4 right-4 z-10 animate-hero-float motion-reduce:animate-none sm:-right-8"
        >
          <div className="flex animate-hero-pop items-center gap-2 rounded-full bg-brand-cream px-4 py-2 text-sm font-semibold text-brand-navy shadow-[4px_4px_0_var(--brand-navy)] motion-reduce:animate-none">
            <span className="h-2 w-2 animate-hero-pulse rounded-full bg-brand-accent motion-reduce:animate-none" />
            {t("chipNewBid")}
          </div>
        </div>
      )}

      {showChips && stage === WINNER_STAGE && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-4 left-4 z-10 animate-hero-float motion-reduce:animate-none sm:-left-8"
        >
          <div className="flex animate-hero-pop items-center gap-2 rounded-full bg-brand-accent px-4 py-2 text-sm font-bold text-brand-navy shadow-[4px_4px_0_var(--brand-navy)] motion-reduce:animate-none">
            <Check className="h-4 w-4" strokeWidth={3} />
            {t("chipWinner")}
          </div>
        </div>
      )}

      <header className="mb-6">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-brand-border-dark px-3 py-1 text-sm font-semibold">
            {SAMPLE_TENDER.company}
          </span>
          <span className="text-sm text-brand-muted-dark">
            {SAMPLE_TENDER.location} · {SAMPLE_TENDER.deadline}
          </span>
        </div>
        <h2 className="min-h-[55px] text-[22px] font-semibold leading-snug">{SAMPLE_TENDER.title}</h2>
      </header>

      <ol className="mb-6">
        {TENDER_STAGES.map((key, index) => {
          const isDone = index < stage;
          const isActive = index === stage;
          const fill = index < stage ? 1 : isActive ? progress : 0;
          const isLast = index === TENDER_STAGES.length - 1;

          return (
            <li key={key} className={cn("relative", !isLast && "pb-4")}>
              {!isLast && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-[17px] top-10 w-0.5 overflow-hidden rounded-full bg-brand-track"
                >
                  <span
                    className="block h-full w-full origin-top bg-brand-accent"
                    style={{ transform: `scaleY(${fill})` }}
                  />
                </span>
              )}
              <button
                type="button"
                onClick={() => onSelectStage(index)}
                aria-current={isActive ? "step" : undefined}
                className="flex w-full items-start gap-4 rounded-xl p-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy"
              >
                <span
                  className={cn(
                    "step-node relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold",
                    isDone && "border-brand-accent bg-brand-accent text-brand-navy",
                    isActive && "border-brand-cream bg-brand-cream text-brand-navy",
                    !isDone && !isActive && "border-brand-border-dark bg-transparent text-brand-muted-dark",
                  )}
                >
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-[-2px] animate-hero-ring rounded-full border-2 border-brand-cream motion-reduce:animate-none"
                    />
                  )}
                  {isDone ? <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" /> : index + 1}
                </span>
                <span className="min-w-0 flex-1 pt-1">
                  <span
                    className={cn(
                      "step-label block text-base font-semibold",
                      isActive || isDone ? "text-brand-cream" : "text-brand-muted-dark",
                    )}
                  >
                    {t(`stages.${key}.title`)}
                  </span>
                  <span
                    className={cn(
                      "block overflow-hidden text-sm text-brand-muted-dark",
                      isActive ? "h-12 pt-1" : "h-0",
                    )}
                    aria-hidden={!isActive}
                  >
                    <span className="block pt-1">{t(`stages.${key}.description`)}</span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <footer className="border-t border-brand-border-dark pt-5">
        <div className="mb-4 flex gap-8">
          <div>
            <span className="block text-2xl font-bold tabular-nums">{counters.bids}</span>
            <span className="text-sm text-brand-muted-dark">{t("bids")}</span>
          </div>
          <div>
            <span className="block text-2xl font-bold tabular-nums">{counters.views}</span>
            <span className="text-sm text-brand-muted-dark">{t("views")}</span>
          </div>
        </div>
        <div className="mb-2 text-sm font-semibold text-brand-muted-dark">{t("progressLabel")}</div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-brand-track"
          role="progressbar"
          aria-label={t("progressLabel")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(overall * 100)}
        >
          <div
            className="h-full w-full origin-left rounded-full bg-brand-accent"
            style={{ transform: `scaleX(${overall})` }}
          />
        </div>
      </footer>
    </article>
  );
}
